import { isChameleonThemeIdError } from '@/core/shared/errors/chameleon-theme-error.util';
import { HttpStatus } from '@/core/shared/errors/enums/http-status.enum';
import { InternalError } from '@/core/shared/errors/internal.error';
import { isTransientNetworkFetchError } from '@/core/shared/errors/network-fetch-error.util';
import { isResizeObserverLoopError } from '@/core/shared/errors/resize-observer-loop-error.util';
import * as Sentry from '@sentry/vue';
import type { EventHint, Event as SentryEvent } from '@sentry/vue';
import type { App } from 'vue';

export default (app: App): void => {
  if (import.meta.env.DEV) {
    return;
  }

  Sentry.init({
    app,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    replaysOnErrorSampleRate: 1.0,
    debug: true,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    beforeSend,
  });
};

export function beforeSend(event: SentryEvent, hint: EventHint): SentryEvent | null {
  const error = hint.originalException;

  // Drop browser-level transient fetch failures (`TypeError: Failed to fetch`,
  // Firefox `NetworkError`, Safari `Load failed`). These are recoverable
  // user-network blips, not actionable bugs. Catches every code path —
  // captureException callers, unhandled rejections, window.onerror, Vue
  // errorHandler, etc. A breadcrumb is left so any subsequent real error
  // carries the network-context tail in its trail.
  if (isTransientNetworkFetchError(error)) {
    Sentry.addBreadcrumb({
      category: 'network',
      message: 'Transient fetch failure (suppressed from Sentry)',
      level: 'warning',
    });
    return null;
  }

  // Drop the harmless browser-level ResizeObserver loop error
  // (`ResizeObserver loop completed with undelivered notifications.` /
  // `ResizeObserver loop limit exceeded`). Browsers raise it through
  // `window.onerror` with no actionable in-app stack trace when a
  // ResizeObserver callback cannot be delivered in a single frame;
  // capturing it pages `#one-vitals` without any user-visible breakage
  // to chase. Leave a breadcrumb so the next real error in the same
  // session still carries the context.
  // Chromium raises this one with `ErrorEvent.error === null`, so the SDK
  // hands us a null `originalException`; fall back to the event's own text.
  if (isResizeObserverLoopError(error) || (error == null && hasResizeObserverLoopText(event))) {
    Sentry.addBreadcrumb({
      category: 'ui.layout',
      message: 'ResizeObserver loop notification (suppressed from Sentry)',
      level: 'warning',
    });
    return null;
  }

  // Drop Chameleon's own `getThemeId is not a function` crash, which floods
  // the project from the widget's CDN-loaded script and exhausts the error quota.
  if (isChameleonThemeIdError(event)) {
    Sentry.addBreadcrumb({
      category: 'vendor',
      message: 'Chameleon getThemeId error (suppressed from Sentry)',
      level: 'warning',
    });
    return null;
  }

  if (error instanceof InternalError) {
    if (error.status === HttpStatus.UNAUTHORIZED) {
      // do not send 401 errors to Sentry
      return null;
    }
  }
  return event;
}

function hasResizeObserverLoopText(event: SentryEvent): boolean {
  return (
    isResizeObserverLoopError(event.message) ||
    (event.exception?.values ?? []).some((value) => isResizeObserverLoopError(value.value))
  );
}
